import React, { useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Globe, Phone } from "lucide-react";
import { Header } from "../../../components/header";
import { CardHeader } from "../../../components/cards";
import { SendIcon } from "../../../components/icons";
import { Loading } from "../../../components/loading";
import { Input, Select, Textarea } from "../../../components/form";
import { Button } from "../../../components/button";
import { useProspection, useUpdateProspection } from "../hooks";
import { CustomerSelectSearch } from "../../customers/components";
import { ProspectionType } from "../../../__generated__/globalTypes";
import { prospectionTypeLabel } from "../../../helpers/enum-labels";

type IUpdateProspection = {
  id: string;
};

export const UpdateProspection: React.FC = () => {
  const { id } = useParams<IUpdateProspection>();
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) {
      navigate("/");
    }
  }, []);

  const { data, refetch } = useProspection(+id!);
  const { form, submit, loading } = useUpdateProspection({
    id: +id!,
    onCompleted: () => {
      refetch();
      toast.success("La prospection a été modifiée avec succès");
    },
  });

  const messageRef = useRef<HTMLTextAreaElement | null>(null);
  const { ref: registerMessageRef, ...messageField } = form.register("message");

  const resizeMessage = () => {
    const el = messageRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    if (data?.prospection?.ok && data?.prospection?.result) {
      const { result } = data.prospection;
      form.setValue("customerId", result.customerId);
      form.setValue("type", result.type);
      form.setValue("automatique", result.automatique);
      form.setValue("phone", result.phone);
      form.setValue("email", result.email);
      form.setValue("message", result.message);
      requestAnimationFrame(resizeMessage);
    }
  }, [data]);

  if (!data) {
    return <Loading />;
  }

  const prospection = data?.prospection?.result;
  const type = form.watch("type");

  return (
    <>
      <Header
        title="Prospection"
        subtitle={prospection ? `#${prospection.id}` : ""}
        buttons={[
          {
            actionText: "Annuler",
            bgColor: "indigo",
            textColor: "white",
            link: `/prospections`,
            icon: <SendIcon />,
          },
        ]}
      />
      <div className="main-container">
        {prospection?.customer && (
          <div className="card mb-4">
            <CardHeader title="Client" />
            <div className="flex flex-wrap items-center gap-4">
              <Link
                to={`/customer/${prospection.customer.id}`}
                className="text-primary-600 font-medium hover:underline"
              >
                {prospection.customer.name}
              </Link>
              {prospection.customer.site && (
                <a
                  href={
                    prospection.customer.site.startsWith("http")
                      ? prospection.customer.site
                      : `https://${prospection.customer.site}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-zinc-400 hover:text-primary-600 hover:underline"
                >
                  <Globe size={14} />
                  {prospection.customer.site}
                </a>
              )}
              {prospection.customer.phone && (
                <a
                  href={`tel:${prospection.customer.phone}`}
                  className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-zinc-400 hover:text-primary-600 hover:underline"
                >
                  <Phone size={14} />
                  {prospection.customer.phone}
                </a>
              )}
            </div>
          </div>
        )}

        <div className="card">
          <CardHeader title="Modifier une prospection" />
          <div className="w-full">
            <div className="flex flex-wrap gap-3">
              <CustomerSelectSearch form={form} name="customerId" label="Client (optionnel)" />
              <Select label="Type" className="w-full" {...form.register("type")}>
                {Object.keys(ProspectionType).map((key) => (
                  <option key={key} value={key}>
                    {prospectionTypeLabel(key)}
                  </option>
                ))}
              </Select>
              {type === ProspectionType.Email ? (
                <Input label="Email" type="email" className="w-full" {...form.register("email")} />
              ) : (
                <Input label="Téléphone" className="w-full" {...form.register("phone")} />
              )}
              <Textarea
                label="Message"
                className="w-full overflow-hidden"
                {...messageField}
                ref={(el) => {
                  registerMessageRef(el);
                  messageRef.current = el;
                }}
                onChange={(e) => {
                  messageField.onChange(e);
                  resizeMessage();
                }}
              />
              <label className="w-full flex items-center gap-2">
                <input type="checkbox" {...form.register("automatique")} />
                <span className="text-sm">Envoi automatique</span>
              </label>
            </div>
            <div className="w-full mt-4">
              <Button onClick={submit} loading={loading} disabled={loading}>
                Valider
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
